import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Leaf, Gift } from "lucide-react";
import SocialLoginButtons from "../components/SocialLoginButtons";
import ContinueAsGuestButton from "../components/ContinueAsGuestButton";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referral, setReferral] = useState(refFromUrl);
  const [referralValid, setReferralValid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!refFromUrl) return;
    // For visitors, validate visually only (the validate endpoint requires auth);
    // we'll let the backend confirm during registration.
    setReferralValid("pending");
  }, [refFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(name, email, password, referral.trim() || undefined);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}!`);
      navigate("/questionnaire", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-leaf-pattern flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-700 text-white mb-3">
            <Leaf className="w-6 h-6" />
          </span>
          <h1 className="font-display text-3xl text-emerald-950 font-light tracking-tight">
            Create your account
          </h1>
          <p className="text-emerald-900/60 mt-2 text-sm">Takes 30 seconds. No card needed.</p>
        </div>
        <Card className="rounded-2xl border-emerald-100">
          <CardContent className="p-6 sm:p-8">
            <SocialLoginButtons intent="signup" />
            <div className="mt-3">
              <ContinueAsGuestButton />
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name" className="text-emerald-900">Your name</Label>
                <Input
                  id="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 rounded-xl border-emerald-200 focus-visible:ring-emerald-500/30"
                  placeholder="Jane Doe"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-emerald-900">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 rounded-xl border-emerald-200 focus-visible:ring-emerald-500/30"
                  placeholder="you@example.com"
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-emerald-900">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 rounded-xl border-emerald-200 focus-visible:ring-emerald-500/30"
                  placeholder="At least 6 characters"
                  data-testid="register-password-input"
                />
              </div>
              {error ? (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="register-error">
                  {error}
                </div>
              ) : null}
              <div>
                <Label htmlFor="ref" className="text-emerald-900 flex items-center gap-2">
                  <Gift className="w-3.5 h-3.5 text-amber-600" /> Referral code (optional)
                </Label>
                <Input
                  id="ref"
                  value={referral}
                  onChange={(e) => setReferral(e.target.value.toUpperCase())}
                  className="mt-1 rounded-xl border-emerald-200 focus-visible:ring-emerald-500/30 font-mono"
                  placeholder="e.g. REF-XXXXXX"
                  data-testid="register-referral-input"
                />
                {referralValid === "pending" && (
                  <p className="mt-1 text-xs text-amber-700">
                    🎁 You'll get 10% off your first plan with this code.
                  </p>
                )}
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 text-white py-6"
                data-testid="register-submit-btn"
              >
                {loading ? "Creating account…" : "Start my assessment"}
              </Button>
              <p className="text-[11px] text-emerald-900/60 text-center leading-relaxed">
                By creating an account you agree to our{" "}
                <Link to="/terms" className="underline hover:text-emerald-700" data-testid="register-terms-link">
                  Terms
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="underline hover:text-emerald-700" data-testid="register-privacy-link">
                  Privacy Policy
                </Link>
                . Wellness guidance only — not medical advice.
              </p>
            </form>
            <p className="mt-6 text-sm text-emerald-900/70 text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-emerald-700 font-semibold hover:underline" data-testid="register-to-login-link">
                Log in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
